export type GroupRole = 'admin' | 'editor' | 'member';

/**
 * MWBI-798 — slugs are now opaque strings sourced from
 * api::group-category.group-category at runtime; do not hard-code the
 * enumeration anywhere. The store fetches the canonical list and every UI
 * dropdown reads from it. Kept as a type alias only so existing call sites
 * don't have to be rewritten.
 */
export type GroupCategory = string;

export interface GroupCategoryRecord {
  id: number;
  documentId: string;
  name: string;
  slug: string;
  order?: number;
  archived?: boolean;
}

export interface GroupVideo {
  url: string;
  title?: string;
}

export interface Group {
  id: number;
  documentId: string;
  name: string;
  description: string;
  // MWBI-798 — canonical group-category relation. Populate with
  // `populate: { categories: { fields: ['id','documentId','name','slug'] } }`.
  // `getGroupCategories(group)` is the only supported read path.
  categories?: GroupCategoryRecord[];
  /**
   * @deprecated Legacy json column kept on the API response for
   * pre-backfill records only. UI must NOT read or write this. After every
   * env has run the seedEssentials backfill, the column will be removed
   * (see project_strapi_inverse_relation memory + MWBI-798 epic).
   */
  category?: GroupCategory | GroupCategory[];
  coverImage?: { id?: number; url: string; alternativeText?: string } | null;
  avatar?: { id?: number; url: string; alternativeText?: string } | null;
  images?: { id: number; url: string; alternativeText?: string }[] | null;
  acceptingMembers: boolean;
  websiteLink?: string;
  tags?: string[];
  videos?: GroupVideo[];
  memberCount?: number;
  eventsCount?: number;
  followers?: { id: number }[];
  isArchived?: boolean;
  // MWBI-739 — true hides the group from the public directory browse list
  // for non-members. memberListVisible/eventListVisible only hide the
  // member/event lists inside the detail page; isPrivate hides the group itself.
  isPrivate?: boolean;
  // TogetherWe-staff-curated: when true, this group is eligible to be picked
  // for the daily/weekly digest's Featured Groups section. Toggled in the
  // admin user-management UI; private groups are excluded regardless.
  featured?: boolean;
  memberListVisible?: boolean;
  eventListVisible?: boolean;
  rulesText?: string;
  memberApprovalMode?: 'auto' | 'manual';
  createdAt: string;
  updatedAt: string;
}

/**
 * Returns the group's category records. Prefers the canonical `categories`
 * relation; falls back to the legacy json `category` only for records that
 * predate the bootstrap backfill (a single-boot transient — the fallback
 * lets a freshly-deployed frontend render correctly before the backfill
 * completes). After deprecation, the fallback can be deleted entirely.
 */
export function getGroupCategories(
  group: Pick<Group, 'categories' | 'category'> | null | undefined,
): GroupCategoryRecord[] {
  if (!group) return [];
  if (Array.isArray(group.categories) && group.categories.length > 0) {
    return group.categories;
  }
  // Legacy fallback: synthesize minimal records from slug strings. No name
  // available, so render the slug as both. Pre-backfill records only.
  const raw = group.category;
  if (!raw) return [];
  const slugs = Array.isArray(raw) ? raw : [raw];
  return slugs
    .filter((slug): slug is string => typeof slug === 'string' && slug.length > 0)
    .map((slug) => ({ id: 0, documentId: `legacy-${slug}`, name: slug, slug }));
}

export function getGroupCategorySlugs(
  group: Pick<Group, 'categories' | 'category'> | null | undefined,
): string[] {
  return getGroupCategories(group).map((c) => c.slug);
}

export interface GroupMember {
  userId: number;
  groupId: number; // Keep for backwards compatibility
  groupDocumentId: string; // Use this for stable relations
  role: GroupRole;
}

export interface UserGroupMembership {
  group: Group;
  role: GroupRole;
}
